/**
 * The medical record's visual vocabulary.
 *
 * Kept separate from the document builder so the presentation can be reworked
 * without touching the data assembly, and so each piece can be checked on its
 * own.
 *
 * Everything here is inline SVG and inline styles. A printed document has no
 * stylesheet to lean on and no icon font it can rely on being installed — emoji
 * were the legacy answer and they render differently on every machine, sometimes
 * in colour and sometimes not. Stroked SVG prints identically everywhere and
 * scales cleanly on a 600dpi printer.
 */

import { escapeHtml } from './prescriptionPrint';

/**
 * Icon paths, lucide-style: 24×24, stroked, no fill.
 * Only the handful the record actually uses, so nothing unused is inlined.
 */
export const ICON_PATHS = {
  calendar:
    'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  'file-text': 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z M14 2v6h6 M16 13H8 M16 17H8',
  pill: 'M10.5 20.5 3.5 13.5a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7Z M8.5 8.5l7 7',
  flask: 'M10 2v7.31 M14 9.3V1.99 M8.5 2h7 M14 9.3a6.5 6.5 0 1 1-4 0 M5.58 16.5h12.85',
  scan: 'M3 7V5a2 2 0 0 1 2-2h2 M17 3h2a2 2 0 0 1 2 2v2 M21 17v2a2 2 0 0 1-2 2h-2 M7 21H5a2 2 0 0 1-2-2v-2',
  image: 'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z M8.5 8.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z M21 15l-5-5L5 21',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
  clipboard: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2 M9 2h6v4H9z M8 11h8 M8 16h6',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z M12 7v5l3 2',
  check: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z M8.5 12.5l2.5 2.5 4.5-5',
  heart: 'M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l8.8 8.8 8.8-8.8a5.5 5.5 0 0 0 0-7.8Z',
  paperclip: 'M21.4 11.05 12.25 20.2a6 6 0 0 1-8.49-8.49l9.2-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48',
  // Added for institution services. Without real paths these fell through to
  // 'activity', and a surgery, a bed and a hospital all printed the same
  // squiggle — which is the opposite of what the icon coding is for.
  scissors: 'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M20 4 8.12 15.88 M14.47 14.48 20 20 M8.12 8.12 12 12',
  bed: 'M2 4v16 M2 8h18a2 2 0 0 1 2 2v10 M2 17h20 M6 8v5',
  'building-2': 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2 M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2 M10 6h4 M10 10h4 M10 14h4 M10 18h4',
};

/**
 * An icon inside a tinted rounded square.
 *
 * The tint is the section's own colour at low alpha, which is what carries the
 * colour-coding through the document — a reader learns that violet means labs
 * and amber means x-rays without being told.
 */
export function iconChip(name, colour, size = 30) {
  const path = ICON_PATHS[name] || ICON_PATHS.activity;
  const inner = Math.round(size * 0.56);
  return `<span style="display:inline-flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:9px;background:${colour}1f;border:1px solid ${colour}40;flex-shrink:0;vertical-align:middle">
    <svg width="${inner}" height="${inner}" viewBox="0 0 24 24" fill="none" stroke="${colour}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      ${path
        .split(' M')
        .map((segment, index) => `<path d="${index === 0 ? segment : `M${segment}`}"/>`)
        .join('')}
    </svg>
  </span>`;
}

/**
 * A section heading: icon chip, title, and a rule that fades out.
 *
 * The rule is a gradient rather than a flat line so a page of stacked sections
 * does not read as a stack of identical boxes.
 */
export function sectionHeading(title, { icon, colour, subtitle = '' }) {
  return `
  <div class="rec-block" style="margin:0 0 14px">
    <div style="display:flex;align-items:center;gap:10px">
      ${iconChip(icon, colour)}
      <div>
        <h2 style="margin:0;font-size:16px;font-weight:900;color:#0f172a;letter-spacing:-.2px">${escapeHtml(title)}</h2>
        ${subtitle ? `<p style="margin:1px 0 0;font-size:11px;color:#94a3b8">${escapeHtml(subtitle)}</p>` : ''}
      </div>
    </div>
    <div style="height:3px;border-radius:3px;margin-top:8px;background:linear-gradient(to left, ${colour}, ${colour}22 70%, transparent)"></div>
  </div>`;
}

/**
 * A statistic tile.
 *
 * The number is the thing being read, so it carries the colour and the weight;
 * the label is deliberately quiet underneath it.
 */
export function statTile({ label, value, colour, icon }) {
  return `
  <div class="rec-block" style="flex:1 1 96px;min-width:96px;border-radius:12px;padding:11px 8px 9px;text-align:center;background:linear-gradient(160deg, ${colour}14, ${colour}05);border:1px solid ${colour}33;box-shadow:0 1px 2px ${colour}14">
    <div style="margin-bottom:4px">${iconChip(icon, colour, 24)}</div>
    <p style="margin:0;font-size:23px;font-weight:900;color:${colour};line-height:1.05">${escapeHtml(value)}</p>
    <p style="margin:2px 0 0;font-size:10.5px;color:#64748b;font-weight:600">${escapeHtml(label)}</p>
  </div>`;
}

/** Status colours for a visit row. */
export const STATUS_STYLE = {
  completed: { label: 'تم الكشف', colour: '#059669' },
  confirmed: { label: 'مؤكد', colour: '#0891b2' },
  in_progress: { label: 'جاري الكشف', colour: '#7c3aed' },
  pending: { label: 'بانتظار الدفع', colour: '#d97706' },
  cancelled: { label: 'ملغي', colour: '#dc2626' },
};

export const statusPill = (status) => {
  const meta = STATUS_STYLE[status] || { label: status || '—', colour: '#64748b' };
  return `<span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:10.5px;font-weight:700;color:${meta.colour};background:${meta.colour}17;border:1px solid ${meta.colour}38;white-space:nowrap">${escapeHtml(meta.label)}</span>`;
};

/**
 * The hero panel at the top of the cover sheet.
 *
 * A gradient wash keyed to the clinic's own colours, so the record opens the
 * same way the prescription does.
 */
export function patientHero({ name, code, phone, issued, primary, secondary }) {
  return `
  <div class="rec-block" style="position:relative;overflow:hidden;border-radius:16px;padding:18px 22px;margin-bottom:16px;background:linear-gradient(120deg, ${primary}18, ${secondary}12 55%, ${primary}08);border:1px solid ${primary}33">
    <div style="position:absolute;top:-46px;left:-30px;width:150px;height:150px;border-radius:50%;background:${secondary}12"></div>
    <div style="position:relative;display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap">
      <div style="display:flex;align-items:center;gap:12px">
        ${iconChip('user', primary, 44)}
        <div>
          <p style="margin:0;font-size:10.5px;color:#64748b;font-weight:700;letter-spacing:.4px">اسم المريض</p>
          <p style="margin:1px 0 0;font-size:22px;font-weight:900;color:#0f172a;line-height:1.15">${escapeHtml(name)}</p>
          ${code ? `<span style="display:inline-block;margin-top:5px;background:white;color:${primary};font-size:11.5px;font-weight:800;padding:2px 9px;border-radius:6px;font-family:monospace;border:1px solid ${primary}44" dir="ltr">${escapeHtml(code)}</span>` : ''}
        </div>
      </div>
      <div style="text-align:left;font-size:11.5px;color:#475569">
        ${phone ? `<p style="margin:0 0 3px" dir="ltr"><strong>${escapeHtml(phone)}</strong></p>` : ''}
        <p style="margin:0;color:#64748b">تاريخ الإصدار: ${escapeHtml(issued)}</p>
      </div>
    </div>
  </div>`;
}

/** A quiet placeholder for a section with nothing in it. */
export const emptyNote = (text) => `
  <div class="rec-block" style="border:1px dashed #cbd5e1;border-radius:10px;padding:16px;text-align:center;color:#94a3b8;font-size:12.5px">
    ${escapeHtml(text)}
  </div>`;

export default { iconChip, sectionHeading, statTile, statusPill, patientHero, emptyNote };
